"use client";

import { Menu, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Button } from "../../../elements/button/button";
import { cn } from "../../../lib/utils";
import {
  mobileNavigationBarClasses,
  mobileNavigationMenuId,
} from "../main-navigation.constants";
import type { MainNavigationAction } from "../main-navigation.types";
import { NavigationActions } from "../navigation-actions/navigation-actions";

export interface MobileNavigationBarProps {
  /** Brand logo, usually wrapped in a link to the homepage. */
  logo: ReactNode;
  /** Header actions such as search, wishlist, cart and account. */
  actions?: MainNavigationAction[];
  /**
   * Action whose panel is open, `null` for none. Pass the same state as to the
   * main navigation to open a panel from outside, e.g. the cart after a
   * product was added. Leave it undefined to let the bar manage it.
   */
  openActionId?: string | null;
  onOpenActionIdChange?: (actionId: string | null) => void;
  isMenuOpen: boolean;
  onMenuToggle: () => void;
  /** Must match the id of the mobile menu panel. */
  menuId?: string;
  className?: string;
  microcopies: {
    openMenu: string;
    closeMenu: string;
  };
}

/**
 * Below the desktop breakpoint the header moves to the bottom of the screen:
 * it carries the logo, the header actions and the menu toggle. It sits above
 * the menu panel so that its actions stay reachable while the menu is open.
 * Action panels and the menu replace each other.
 */
export function MobileNavigationBar(props: Readonly<MobileNavigationBarProps>) {
  const [ownOpenActionId, setOwnOpenActionId] = useState<string>();
  const openActionId =
    props.openActionId === undefined
      ? ownOpenActionId
      : (props.openActionId ?? undefined);

  function setOpenActionId(actionId: string | undefined) {
    setOwnOpenActionId(actionId);
    props.onOpenActionIdChange?.(actionId ?? null);
  }

  function toggleActionPanel(actionId: string) {
    const nextActionId = actionId === openActionId ? undefined : actionId;
    if (nextActionId && props.isMenuOpen) {
      props.onMenuToggle();
    }
    setOpenActionId(nextActionId);
  }

  function handleMenuToggle() {
    if (openActionId && !props.isMenuOpen) {
      setOpenActionId(undefined);
    }
    props.onMenuToggle();
  }

  return (
    <div
      className={cn(
        "fixed inset-x-0 bottom-0 z-50 flex items-center justify-between border-alabastergrey border-t bg-white px-8 lg:hidden",
        mobileNavigationBarClasses.height,
        props.className
      )}
      data-testid="mobile-navigation-bar"
    >
      {props.logo}
      <div className="flex items-center gap-1">
        {props.actions ? (
          <NavigationActions
            actions={props.actions}
            onClose={() => setOpenActionId(undefined)}
            onToggle={toggleActionPanel}
            openActionId={openActionId}
            placement="mobile"
          />
        ) : null}
        <Button
          aria-controls={props.menuId ?? mobileNavigationMenuId}
          aria-expanded={props.isMenuOpen}
          aria-label={
            props.isMenuOpen
              ? props.microcopies.closeMenu
              : props.microcopies.openMenu
          }
          // The design shows the close state as a filled circle.
          className="rounded-full"
          data-testid="mobile-navigation-toggle"
          mode="D2C"
          onClick={handleMenuToggle}
          size="icon"
          variant={props.isMenuOpen ? "secondary" : "ghost"}
        >
          {props.isMenuOpen ? (
            <X aria-hidden="true" />
          ) : (
            <Menu aria-hidden="true" />
          )}
        </Button>
      </div>
    </div>
  );
}
